import { Box, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import SwitchToggle from "../../components/SwitchToggle";
import { useGetSubAdminListQuery, useDeleteSubAdminMutation } from "../../store/api/manageAPI";
import Pagination from "../../components/Panigation";
import moment from "moment";
import { USER_TYPES } from "../../constants/enums";
import { showToast, showError } from "../../constants/toasts";
import DeleteConfirmationModal from "../../Modals/deleteConfirmation";

const getRoleLabel = (role: number) => {
  const entry = Object.entries(USER_TYPES).find(([_, value]) => value === role);
  return entry ? entry[0].replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) : "Unknown";
};

const SubadminList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [searchTerm] = useState("");

  const { data: subAdminData, isLoading, isFetching } = useGetSubAdminListQuery({
    page: page,
    limit: rowsPerPage,
    search: searchTerm,
  });

  const [deleteSubAdmin, { isLoading: isDeleting }] = useDeleteSubAdminMutation();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState<string | null>(null);

  const rows = subAdminData?.data?.list || [];
  const totalCount = subAdminData?.data?.totalCount || 0;

  const columns = [
    { id: 'name', label: 'Name' },
    { id: 'email', label: 'Email' },
    { id: 'phone', label: 'Phone Number' },
    { id: 'role', label: 'Role' },
    { id: 'date', label: 'Created Date' },
    { id: 'status', label: 'Status' },
    { id: 'action', label: 'Action' },
  ];

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleDeleteClick = (id: string) => {
    setSelectedDeleteId(id);
    setDeleteModalOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteModalOpen(false);
    setSelectedDeleteId(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedDeleteId) return;

    try {
      const response = await deleteSubAdmin(selectedDeleteId).unwrap();
      if (response && (response.success || response.status === 'success' || response.message)) {
        showToast(response.message || "Sub-Admin deleted successfully");
        handleDeleteClose();
      } else {
        showError(response.message || "Failed to delete Sub-Admin");
      }
    } catch (error: any) {
      showError(error?.data?.message || "Failed to delete Sub-Admin");
    }
  };

  return (
    <>
      <TableContainer className="table_container">
        <Table>
          <TableHead>
            <TableRow hover>
              {columns.map((column) => (
                <TableCell key={column.id}>{column.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading || isFetching ? (
              <TableRow>
                <TableCell colSpan={7} align="center" style={{ padding: "50px" }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : rows.length > 0 ? (
              rows.map((row: any) => (
                <TableRow hover key={row._id}>
                  <TableCell>
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/manage-subadmin/detail/${row._id}`);
                      }}
                      className="user_block click_action"
                      style={{ cursor: "pointer" }}
                    >
                      <figure>
                        <img
                          src={row.image || "/static/images/user_placeholder.png"}
                          alt="User"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = "/static/images/user_placeholder.png";
                          }}
                        />
                      </figure>
                      <p><b>{row.fullName || row.firstName + " " + row.lastName}</b></p>
                    </div>

                  </TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{row.countryCode} {row.phone}</TableCell>
                  <TableCell>{getRoleLabel(row.role)}</TableCell>
                  <TableCell>{moment(row.createdAt).format("DD MMMM YYYY")}</TableCell>
                  <TableCell>
                    <SwitchToggle checked={!row.isBlocked} />
                  </TableCell>
                  <TableCell>
                    <div className="table_actions v2">
                      <Box>
                        <IconButton onClick={() => navigate(`/manage-subadmin/detail/${row._id}`)}>
                          <img src="/static/images/eye_icon.svg" alt="" />
                        </IconButton>
                      </Box>
                      <Box>
                        <IconButton onClick={() => navigate(`/manage-subadmin/edit/${row._id}`)}>
                          <img src="/static/images/edit2_icon.svg" alt="" />
                        </IconButton>
                      </Box>
                      <Box>
                        <IconButton onClick={() => handleDeleteClick(row._id)}>
                          <img src="/static/images/trash2_icon.svg" alt="" />
                        </IconButton>
                      </Box>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No records found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!isLoading && totalCount > 0 && (
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Pagination
            module={null}
            page={page}
            setPage={setPage as any}
            onPageChange={handlePageChange}
            totalPages={Math.ceil(totalCount / rowsPerPage)}
            totalCount={totalCount}
            limit={rowsPerPage}
          />
        </Box>
      )}

      <DeleteConfirmationModal
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        onClose={handleDeleteClose}
        onConfirm={handleConfirmDelete}
        itemName={rows.find((r: any) => r._id === selectedDeleteId)?.fullName || "Sub-Admin"}
        isLoading={isDeleting}
      />
    </>
  );
};

export default SubadminList;